// A person's SEPARATE consents (#5881, decided on #5875): consents the law
// requires apart from the Terms, one row per `(user, kind)` in `user_consents`.
//
// The first kind is the cross-border transfer consent PIPL Art. 38-39 requires
// of a user in mainland China: their personal information is stored and
// processed outside the PRC, and that needs its own notice and its own act,
// never a box ticked together with the ToS. ToS consent stays on `users` for
// now; moving it here is its own change.
//
// ONE RULE, ONE PLACE. Whether a person is missing a consent the platform must
// hold before it serves them is {@link missingConsent}. Every door that caps a
// credential (the session resolver and `resolveApiToken`) reads it, so a cookie
// and a PAT cannot disagree about the same human.

import { z } from 'zod';
import type { RegionCode } from '../value-objects/region-code';
import { CROSS_BORDER_CONSENT_VERSION } from './account';

/**
 * The kinds of separate consent. Stored as text and restated by the
 * `user_consents_kind_ck` CHECK, which SQL needs because it cannot import this.
 */
export const ConsentKind = {
  /** PIPL cross-border transfer consent, asked of `region = 'CN'` users only. */
  CrossBorderCn: 'cross_border_cn',
} as const;
export const ConsentKindSchema = z.enum(ConsentKind);
export type ConsentKind = z.infer<typeof ConsentKindSchema>;

/**
 * The region whose users owe the cross-border consent: MAINLAND China. Hong
 * Kong, Macau and Taiwan are `HK` / `MO` / `TW` and are not in scope, matching
 * the zh terms' governing-law clause.
 */
export const CROSS_BORDER_REGION: RegionCode = 'CN';

/**
 * One stored consent. `withdrawnAt` set = withdrawn; a regrant clears it and
 * restamps `version` + `grantedAt`, so the row is the CURRENT state and the
 * audit log is the history (the posture `users.tos_accepted_*` already takes).
 */
export const UserConsentSchema = z.object({
  userId: z.string().uuid(),
  kind: ConsentKindSchema,
  /** The notice version the person was shown when they granted it. */
  version: z.string().min(1),
  grantedAt: z.date(),
  withdrawnAt: z.date().nullable(),
});
export type UserConsent = z.infer<typeof UserConsentSchema>;

/**
 * The NAMED reason a human's credential is capped to `read:account` because a
 * separate consent is missing. A closed union rather than a boolean, so the
 * next kind adds a member and every reader that switches on it is a type error
 * until it says what the new one means.
 */
export const MissingConsentSchema = z.enum(['missing_cross_border_consent']);
export type MissingConsent = z.infer<typeof MissingConsentSchema>;

/**
 * Is this person missing a consent the platform must hold before serving them?
 *
 * `region = null` (never asked) is NEVER capped: the question is asked by the
 * dashboard, and capping on silence would lock out every user who signed up
 * before it existed. Only a declared `CN` owes the consent, and it is current
 * only when it is unwithdrawn AND for the current notice version, so a version
 * bump re-asks every `CN` user (`CROSS_BORDER_CONSENT_VERSION`'s docblock).
 */
export function missingConsent(input: {
  region: string | null;
  crossBorder: { version: string; withdrawnAt: Date | null } | null;
}): MissingConsent | null {
  if (input.region !== CROSS_BORDER_REGION) return null;
  const c = input.crossBorder;
  const current =
    c !== null && c.withdrawnAt === null && c.version === CROSS_BORDER_CONSENT_VERSION;
  return current ? null : 'missing_cross_border_consent';
}

/** The rung holding a human's credential at `read:account`, by name. */
export type AccessBlocker = 'unapproved' | 'tos_stale' | MissingConsent;

/**
 * WHICH rung capped a credential, outermost first: approval, then the ToS, then a
 * missing separate consent. ONE statement of the ladder's order for every reader
 * that names the cap — the MCP `whoami` `blocked_by`, the OAuth authorize refusal
 * and the `auth.platform_admin_withheld` audit — so none of them answers a CN
 * user's missing consent with the ToS sentence.
 *
 * `null` = nothing blocks: every rung passed, or none applies (`null` booleans
 * are a machine credential's, or unreported — never read as "no").
 */
export function accessBlocker(access: {
  approved: boolean | null;
  tosCurrent: boolean | null;
  missingConsent?: MissingConsent;
}): AccessBlocker | null {
  if (access.approved === false) return 'unapproved';
  if (access.tosCurrent === false) return 'tos_stale';
  return access.missingConsent ?? null;
}
